import ProTable, { ActionType } from '@ant-design/pro-table';
import { ExportableColumn } from '../../../shared/Exporter';
import { useContext, useLayoutEffect, useRef, useState } from 'react';
import { ContextApp } from '../../../contexts/ContextApp';
import useDocumentService from '../../../hooks/useDocumentService';
import { getPaginationArgs } from '../../../shared/getPaginationArgs';
import { Tag } from 'antd';
import { IStatus } from '../../../interfaces/Receipt';
import MomentTimezoneService from '../../../services/moment-timezone/MomentTimezoneService';
import moment from 'moment';
import ViewDocumentModal from './ViewDocumentModal/ViewDocumentModal';
import ButtonPrimary from '../Buttons/ButtonPrimary';
import { Tools } from '../../../shared';
import { IFileData } from '../../../services/graphql/GraphqlService';
import ButtonAction from '../Buttons/ButtonAction';
import useGetElementDimensions from '../../../hooks/useGetElementDimensions';
import './DocumentTable.less';
import { notificationContext } from '../../../contexts/NotificationContext';
import { CustomMessage } from '../../../hooks';
import {
  DocumentTypeEnum,
  ExportReceiptHeadersMapKeys,
} from '../../../enums/EnumsValues';

interface DocumentTableProps {
  columns?: ExportableColumn<any>[];
  actionRef?: React.MutableRefObject<ActionType | undefined>;
  documentType: DocumentTypeEnum;
  exportMapKey?: ExportReceiptHeadersMapKeys;
}

const DocumentTable = (props: DocumentTableProps) => {
  const {
    columns: customColumns,
    actionRef: externalActionRef,
    documentType,
    exportMapKey,
  } = props;

  const { t, selectedTenantId } = useContext(ContextApp);
  const { openNotification } = useContext(notificationContext);
  const { getDocuments, exportDocuments, getDocumentsCount } =
    useDocumentService();
  const { getDate } = MomentTimezoneService();
  const { width } = useGetElementDimensions('.btn-refresh');
  const { getErrorMessage } = CustomMessage();

  const [viewDocumentModal, setViewDocumentModal] = useState({
    open: false,
    data: undefined,
  });
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [pulseAnimation, setPulseAnimation] = useState<boolean>(false);
  const animatedButton = useRef<any>(null);
  const disabledRefresh = useRef<boolean>(false);
  const internalActionRef = useRef<ActionType>();

  // Usar el actionRef externo si se proporciona, de lo contrario usar el interno
  const actionRef = externalActionRef || internalActionRef;

  const columns: ExportableColumn<any>[] = [
    {
      export: true,
      dataIndex: 'id',
      title: t('entity.id'),
      // sorter: true,
      render: (__: any, record: any) => record.id,
      align: 'left',
      hideInForm: true,
      hideInSearch: true,
      hideInTable: true,
      width: 70,
    },
    {
      export: true,
      dataIndex: 'its',
      title: t('entity.uploadDate'),
      // sorter: true,
      render: (_, record) =>
        record.its
          ? getDate({ element: moment(record.its).toDate() }) || '-'
          : '-',
      align: 'left',
      hideInForm: true,
      hideInSearch: true,
    },
    {
      export: true,
      dataIndex: 'filename',
      title: t('entity.filename'),
      // sorter: true,
      render: (_, record) => {
        const displayName = record.filename.replace(/\.\w+$/, '');
        return <span>{displayName}</span>;
      },
      align: 'left',
      hideInForm: true,
      hideInSearch: true,
    },
    {
      export: true,
      dataIndex: 'status',
      title: t('entity.status'),
      // sorter: true,
      renderDataExport: (record: { status: IStatus }) =>
        t(record.status?.translation_key as never, {
          defaultValue: record.status?.name || '-',
        }),
      render: (_, record) => {
        let tagText = t(record.status.translation_key as never, {
          defaultValue: record.status.name || '-',
        });

        return <Tag color={record.status.color}>{tagText}</Tag>;
      },
      align: 'left',
      hideInForm: true,
      hideInSearch: true,
    },
    {
      export: true,
      dataIndex: 'document_type',
      title: t('entity.documentType'),
      // sorter: true,
      render: (_, record) => <span>{record.document_type.name}</span>,
      align: 'left',
      hideInForm: true,
      hideInSearch: true,
    },
  ];

  function handleSelectedRows(
    selectedRowKeys: React.Key[],
    _selectedRows: any[],
  ) {
    setSelectedRowKeys(selectedRowKeys as number[]);
  }

  const request = async (params: any) => {
    try {
      const { skip, take } = getPaginationArgs(
        params.pageSize || 20,
        params.current,
      );

      const documents = await getDocuments({
        tenant_id: selectedTenantId!,
        document_type: documentType,
        skip,
        take,
      });

      const countResult = await getDocumentsCount({
        tenant_id: selectedTenantId!,
        document_type: documentType,
      });
      const totalDocuments =
        countResult.__typename === 'DocumentCount' ? countResult.count : 0;

      return {
        data: documents,
        success: true,
        total: totalDocuments,
      };
    } catch (error) {
      console.log(error);
      return {
        data: [],
        success: false,
        total: 0,
      };
    }
  };

  const exportDocument = async ({
    format,
    tenantId,
    documentType,
    exportMapKey,
  }: {
    format: string;
    tenantId: number;
    documentType?: DocumentTypeEnum;
    exportMapKey?: ExportReceiptHeadersMapKeys;
  }) => {
    try {
      let filters: number[] = [];
      if (selectedRowKeys.length) {
        filters = selectedRowKeys;
      }
      const data: IFileData = await exportDocuments({
        format,
        tenantId,
        documentType,
        exportMapKey,
        filters,
      });
      if (data) {
        Tools.downloadFile(data);
      }
    } catch (error) {
      openNotification({
        msj: getErrorMessage(error),
        type: 'error',
      });
    }
  };

  const refreshTableClick = async (activePulse: boolean) => {
    disabledRefresh.current = true;
    setPulseAnimation(activePulse);
    await actionRef.current?.reload();
    disabledRefresh.current = false;
  };

  useLayoutEffect(() => {
    if (animatedButton.current) {
      animatedButton.current.style.width = `${width}px`;
    }
  }, [width]);

  return (
    <>
      <ProTable
        actionRef={actionRef}
        columns={customColumns ? customColumns : columns}
        search={false}
        rowKey="id"
        toolBarRender={(_) => [
          selectedRowKeys && selectedRowKeys.length !== 0 && (
            <div key="receipt-selector">
              {selectedRowKeys.length > 1 ? (
                <span className="receipt-selector-info">
                  {selectedRowKeys.length} {t('label.selectedPlural')}
                </span>
              ) : (
                <span className="receipt-selector-info">
                  {selectedRowKeys.length} {t('label.selected')}
                </span>
              )}
            </div>
          ),
          <div className="document-table__toolbar-whitespace"></div>,
          <ButtonPrimary
            className="ant-btn ant-btn-primary"
            onClick={() =>
              exportDocument({
                format: 'xlsx',
                tenantId: selectedTenantId!,
                documentType,
                exportMapKey,
              })
            }
          >
            {t('action.download')}
          </ButtonPrimary>,
          <div>
            <div
              ref={animatedButton}
              className={`${pulseAnimation ? 'bg-pulse' : ''}`}
            ></div>
            <ButtonAction
              className="btn-refresh"
              disabled={disabledRefresh.current}
              onClick={() => refreshTableClick(false)}
            >
              <span
                className="material-symbols-outlined refresh-icon"
                translate="no"
              >
                cached
              </span>
            </ButtonAction>
          </div>,
        ]}
        options={{
          reload: false,
          density: false,
        }}
        request={async (params, sorter, filter) =>
          request({ ...params, sorter, filter })
        }
        onRow={(record) => {
          return {
            style: {
              cursor: 'pointer',
            },
            onClick: () => {
              setViewDocumentModal({
                open: true,
                data: record,
              });
            },
          };
        }}
        rowSelection={{
          type: 'checkbox',
          onChange: handleSelectedRows,
          selectedRowKeys: selectedRowKeys,
          preserveSelectedRowKeys: true,
        }}
        tableAlertRender={false}
      />
      {viewDocumentModal.open && (
        <ViewDocumentModal
          state={viewDocumentModal}
          onCancel={() => {
            setViewDocumentModal({
              open: false,
              data: undefined,
            });
          }}
        />
      )}
    </>
  );
};

export default DocumentTable;
